import { environment } from 'src/environments/environment';

export interface SEOInterface {
  title?: string;
  author?: string;
  socialImage?: string;
  description?: string;
  keywords?: string;
  robots_index?: boolean;
  robots_follow?: boolean;
  robots_advanced?: boolean;
  canonical_url?: string;
}

export interface SEOSchemaAddress {
  '@type': string;
  streetAddress: string;
  addressLocality: string;
  postalCode: string;
  addressCountry: string;
}

export interface SEOSchemaBrand {
  logo?: string;
  name?: string;
}

export interface SEOSchemaAuthor {
  '@type': string;
  name: string;
}

export interface SEOSchemaLogo {
  '@type': string;
  url: string;
}

export interface SEOSchemaPublisher {
  '@type': string;
  name: string;
  logo?: SEOSchemaLogo;
}

export interface SEOSchemaItem {
  '@type': string;
  name: string;
}

export interface SEOSchemaListItem {
  '@type': string;
  position: number;
  item: SEOSchemaItem;
}

export interface SEOSchemaLocation {
  '@type': string;
  name: string;
  address: SEOSchemaAddress;
}

export interface SEOSchemaOpeningHours {
  '@type': string;
  dayOfWeek: string[];
  opens: string;
  closes: string;
}

export interface SEOSchemaAnswer {
  '@type': string;
  text: string;
}

export interface SEOSchemaMainEntity {
  '@type': string;
  name: string;
  acceptedAnswer: SEOSchemaAnswer;
}

export interface SEOSchemaPageEntity {
  '@type': string;
  '@id': string;
}

export interface SEOSchema {
  '@context': string;
  '@type': string;
  '@id'?: string;
  name?: string;
  description?: string;
  datePublished?: string;
  dateModified?: string;
  author?: SEOSchemaAuthor;
  publisher?: SEOSchemaPublisher;
  thumbnailUrl?: string;
  image?: string | string[];
  headline?: string;
  brand?: SEOSchemaBrand;
  email?: string;
  telephone?: string;
  sameAs?: string[];
  numberOfItems?: number;
  itemListElement?: SEOSchemaListItem[];
  startDate?: string;
  endDate?: string;
  logo?: string;
  url?: string;
  priceRange?: string;
  location?: SEOSchemaLocation;
  openingHoursSpecification?: SEOSchemaOpeningHours;
  mainEntity?: SEOSchemaMainEntity[];
  mainEntityOfPage?: SEOSchemaPageEntity;
  address?: SEOSchemaAddress;
}

export const SEOStoreCategory = {
  // Canal Walk
  canalwalk: {
    default_title: 'Shops in Cape Town | ' + environment.environment_title,
    default_description:
      'Explore a diverse array of shops at Canal Walk, offering everything from fashion and electronics to dining and entertainment.',
    title_extention: ' | Cape Town | ' + environment.environment_title,

    'banks-and-forex':
      'Conveniently manage your finances at Canal Walk. Discover banks and forex services to meet your financial needs in Cape Town.',
    'books-cards-and-gifts':
      'Find literary treasures, greeting cards, and thoughtful gifts at our books, cards, and gifts shops in Canal Walk, Cape Town.',
    'cellular-service':
      'Stay connected with the latest mobile devices and service plans. Explore cellular service providers at Canal Walk in Cape Town.',
    'centre-management':
      'Get in touch with our dedicated center management team at Canal Walk, ensuring a seamless and enjoyable shopping experience in Cape Town.',
    'department-stores':
      'Discover a world of choices at our department stores in Canal Walk. Shop for fashion, home goods, and more, all under one roof in Cape Town.',
    'eyewear-and-optometrists':
      'Enhance your vision and style with top eyewear brands and professional optometrists at Canal Walk, Cape Town. See the world clearly.',
    'fashion-and-footwear':
      'Elevate your style with the latest fashion trends and footwear collections at Canal Walk, the ultimate shopping destination in Cape Town.',
    'childrens-fashion':
      "Dress your little ones in style! Explore a delightful selection of children's fashion at Canal Walk, your go-to shopping spot in Cape Town.",
    footwear:
      'Step into the world of quality footwear at Canal Walk, Cape Town. Find the perfect pair for every occasion and step out in confidence.',
    'mens-fashion':
      "Discover the latest trends in men's fashion at Canal Walk. Explore a wide range of stylish clothing and accessories tailored for the modern man.",
    sneakers:
      'Step up your shoe game with our diverse collection of sneakers at Canal Walk. Find the perfect pair to elevate your style and comfort.',
    'unisex-fashion':
      'Unleash your fashion freedom with our unisex fashion offerings at Canal Walk. Explore clothing and accessories designed for all genders.',
    'womens-fashion':
      "Elevate your wardrobe with the latest in women's fashion at Canal Walk. From chic dresses to trendy accessories, find your perfect look here.",
    'food-and-beverages':
      "Satisfy your cravings at Canal Walk's food and beverage hub. Discover a wide array of dining options, from delectable meals to refreshing drinks.",
    'coffee-shops':
      "Enjoy a delightful coffee experience at Canal Walk's coffee shops. Sip on freshly brewed coffee and indulge in sweet treats in a cozy atmosphere.",
    'fast-food-and-confectionary':
      "For a quick and tasty meal, head to Canal Walk's fast food and confectionery outlets. Treat yourself to mouthwatering delights that satisfy cravings.",
    'groceries-and-supermarkets':
      "Shop for your daily essentials at Canal Walk's groceries and supermarkets. Find a variety of products and top-quality goods for your convenience.",
    halaal:
      'Discover a diverse range of Halaal dining options at Canal Walk. Enjoy delicious meals prepared in accordance with Islamic dietary guidelines.',
    'health-food':
      "Nourish your body with wholesome choices at Canal Walk's health food outlets. Find organic and nutritious options to support your well-being.",
    liquor:
      "Discover a wide selection of fine wines, spirits, and more at Canal Walk's liquor shops. Find the perfect drink for every occasion.",
    restaurants:
      "Indulge your taste buds at Canal Walk's restaurants. From casual dining to gourmet experiences, savor a variety of delicious cuisines.",
    speciality:
      'Explore unique and specialty shops at Canal Walk. From artisanal crafts to rare finds, our speciality stores offer one-of-a-kind treasures.',
    'takeaway-and-delivery-services':
      'Enjoy convenience with our takeaway and delivery services. Discover a range of culinary delights you can savor from the comfort of your own home at Canal Walk.',
    'health-hair-and-beauty':
      "Enhance your well-being and beauty at Canal Walk's health, hair, and beauty shops. Find professional services and quality products.",
    barber:
      'Visit our barber shop at Canal Walk for grooming services tailored to your style. Experience expert care and precision in a relaxed environment.',
    'beauty-salon':
      'Pamper yourself at our beauty salon in Canal Walk. Unwind and revitalize with a range of beauty treatments and services.',
    'fragrances-and-cosmetics':
      "Discover a world of scents and beauty at Canal Walk's fragrances and cosmetics shops. Find the perfect products to enhance your natural beauty.",
    'hair-salon':
      'Transform your hairstyle at our hair salon in Canal Walk. Experience the latest trends and expert styling from our professional hairdressers.',
    'health-and-wellness':
      "Prioritize your health and well-being with a visit to Canal Walk's health and wellness shops. Explore a range of products and services at Canal Walk.",
    'home-and-decor':
      'Discover a world of style and inspiration for your home at Canal Walk. Explore our unique shops offering a wide range of home and decor products.',
    'jewellery-and-accessories':
      'Elevate your style with exquisite jewelry and fashionable accessories at Canal Walk. Come and explore our diverse range of shops.',
    'movies-and-entertainment':
      'Get ready for a world of entertainment at Canal Walk. Explore our shops that cater to movie enthusiasts and provide entertainment for all ages.',
    'new-stores':
      'Stay updated with the latest additions to Canal Walk. Explore our new stores and be the first to experience fresh and exciting shopping opportunities.',
    'speciality-shops-and-services':
      'Discover unique specialty shops and essential services at Canal Walk. Explore our diverse range of options to meet your specific needs and interests.',
    'leather-and-luggage':
      'Find the perfect blend of style and functionality in our leather and luggage shops at Canal Walk. Explore high-quality products to accompany you.',
    'pet-shops':
      "Pamper your furry friends with the best at Canal Walk's pet shops. Explore a wide variety of pet products and services to keep them happy and healthy.",
    'tobacconists-and-e-cigarettes':
      "Explore a world of flavors and options at Canal Walk's tobacconists and e-cigarette shops.",
    travel:
      "Plan your next adventure with ease at Canal Walk's travel shops. Explore a range of services and products to make your travel experience unforgettable.",
    'sport-fitness-and-lifestyle':
      'Elevate your fitness and lifestyle with our wide range of sports and fitness shops. Explore products and services that help you lead a healthy and active life.',
    technology:
      'Explore the latest in cutting-edge technology at Canal Walk. Discover a wide range of electronic gadgets and devices at our technology stores.',
    'toys-and-hobbies':
      "Find the perfect gift at Canal Walk's Toys and Hobbies shops. Dive into a world of fun with our selection of toys, games, and hobby supplies.",
  },

  // Cape Gate
  capegate: {
    default_title: 'Shops in Cape Town | ' + environment.environment_title,
    default_description:
      'Explore a diverse array of shops at Cape Gate, offering everything from fashion and electronics to dining and entertainment.',
    title_extention: ' | Cape Town | ' + environment.environment_title,

    'banks-and-forex':
      'Conveniently manage your finances at Cape Gate. Discover banks and forex services to meet your financial needs in Cape Town.',
    'books-cards-and-gifts':
      'Find literary treasures, greeting cards, and thoughtful gifts at our books, cards, and gifts shops in Cape Gate, Cape Town.',
    'cellular-service':
      'Stay connected with the latest mobile devices and service plans. Explore cellular service providers at Cape Gate in Cape Town.',
    'centre-management':
      'Get in touch with our dedicated center management team at Cape Gate, ensuring a seamless and enjoyable shopping experience in Cape Town.',
    'department-stores':
      'Discover a world of choices at our department stores in Cape Gate. Shop for fashion, home goods, and more, all under one roof in Cape Town.',
    'eyewear-and-optometrists':
      'Enhance your vision and style with top eyewear brands and professional optometrists at Cape Gate, Cape Town. See the world clearly.',
    'fashion-and-footwear':
      'Elevate your style with the latest fashion trends and footwear collections at Cape Gate, the ultimate shopping destination in Cape Town.',
    'childrens-fashion':
      "Dress your little ones in style! Explore a delightful selection of children's fashion at Cape Gate, your go-to shopping spot in Cape Town.",
    footwear:
      'Step into the world of quality footwear at Cape Gate, Cape Town. Find the perfect pair for every occasion and step out in confidence.',
    'mens-fashion':
      "Discover the latest trends in men's fashion at Cape Gate. Explore a wide range of stylish clothing and accessories tailored for the modern man.",
    sneakers:
      'Step up your shoe game with our diverse collection of sneakers at Cape Gate. Find the perfect pair to elevate your style and comfort.',
    'unisex-fashion':
      'Unleash your fashion freedom with our unisex fashion offerings at Cape Gate. Explore clothing and accessories designed for all genders.',
    'womens-fashion':
      "Elevate your wardrobe with the latest in women's fashion at Cape Gate. From chic dresses to trendy accessories, find your perfect look here.",
    'food-and-beverages':
      "Satisfy your cravings at Cape Gate's food and beverage hub. Discover a wide array of dining options, from delectable meals to refreshing drinks.",
    'coffee-shops':
      "Enjoy a delightful coffee experience at Cape Gate's coffee shops. Sip on freshly brewed coffee and indulge in sweet treats in a cozy atmosphere.",
    'fast-food-and-confectionary':
      "For a quick and tasty meal, head to Cape Gate's fast food and confectionery outlets. Treat yourself to mouthwatering delights that satisfy cravings.",
    'groceries-and-supermarkets':
      "Shop for your daily essentials at Cape Gate's groceries and supermarkets. Find a variety of products and top-quality goods for your convenience.",
    halaal:
      'Discover a diverse range of Halaal dining options at Cape Gate. Enjoy delicious meals prepared in accordance with Islamic dietary guidelines.',
    'health-food':
      "Nourish your body with wholesome choices at Cape Gate's health food outlets. Find organic and nutritious options to support your well-being.",
    liquor:
      "Discover a wide selection of fine wines, spirits, and more at Cape Gate's liquor shops. Find the perfect drink for every occasion.",
    restaurants:
      "Indulge your taste buds at Cape Gate's restaurants. From casual dining to gourmet experiences, savor a variety of delicious cuisines.",
    speciality:
      'Explore unique and specialty shops at Cape Gate. From artisanal crafts to rare finds, our speciality stores offer one-of-a-kind treasures.',
    'takeaway-and-delivery-services':
      'Enjoy convenience with our takeaway and delivery services. Discover a range of culinary delights you can savor from the comfort of your own home at Cape Gate.',
    'health-hair-and-beauty':
      "Enhance your well-being and beauty at Cape Gate's health, hair, and beauty shops. Find professional services and quality products.",
    barber:
      'Visit our barber shop at Cape Gate for grooming services tailored to your style. Experience expert care and precision in a relaxed environment.',
    'beauty-salon':
      'Pamper yourself at our beauty salon in Cape Gate. Unwind and revitalize with a range of beauty treatments and services.',
    'fragrances-and-cosmetics':
      "Discover a world of scents and beauty at Cape Gate's fragrances and cosmetics shops. Find the perfect products to enhance your natural beauty.",
    'hair-salon':
      'Transform your hairstyle at our hair salon in Cape Gate. Experience the latest trends and expert styling from our professional hairdressers.',
    'health-and-wellness':
      "Prioritize your health and well-being with a visit to Cape Gate's health and wellness shops. Explore a range of products and services at Cape Gate.",
    'home-and-decor':
      'Discover a world of style and inspiration for your home at Cape Gate. Explore our unique shops offering a wide range of home and decor products.',
    'jewellery-and-accessories':
      'Elevate your style with exquisite jewelry and fashionable accessories at Cape Gate. Come and explore our diverse range of shops.',
    'movies-and-entertainment':
      'Get ready for a world of entertainment at Cape Gate. Explore our shops that cater to movie enthusiasts and provide entertainment for all ages.',
    'new-stores':
      'Stay updated with the latest additions to Cape Gate. Explore our new stores and be the first to experience fresh and exciting shopping opportunities.',
    'speciality-shops-and-services':
      'Discover unique specialty shops and essential services at Cape Gate. Explore our diverse range of options to meet your specific needs and interests.',
    'leather-and-luggage':
      'Find the perfect blend of style and functionality in our leather and luggage shops at Cape Gate. Explore high-quality products to accompany you.',
    'pet-shops':
      "Pamper your furry friends with the best at Cape Gate's pet shops. Explore a wide variety of pet products and services to keep them happy and healthy.",
    'tobacconists-and-e-cigarettes':
      "Explore a world of flavors and options at Cape Gate's tobacconists and e-cigarette shops. ",
    travel:
      "Plan your next adventure with ease at Cape Gate's travel shops. Explore a range of services and products to make your travel experience unforgettable.",
    'sport-fitness-and-lifestyle':
      'Elevate your fitness and lifestyle with our wide range of sports and fitness shops. Explore products and services that help you lead a healthy and active life.',
    technology:
      'Explore the latest in cutting-edge technology at Cape Gate. Discover a wide range of electronic gadgets and devices at our technology stores.',
    'toys-and-hobbies':
      "Find the perfect gift at Cape Gate's Toys and Hobbies shops. Dive into a world of fun with our selection of toys, games, and hobby supplies.",
  },

  // Clearwater
  clearwater: {
    default_title: 'Shops in Roodepoort | ' + environment.environment_title,
    default_description:
      'Explore a diverse array of shops at Clearwater, offering everything from fashion and electronics to dining and entertainment.',
    title_extention: ' | Roodepoort | ' + environment.environment_title,

    'banks-and-forex':
      'Conveniently manage your finances at Clearwater. Discover banks and forex services to meet your financial needs in Roodepoort.',
    'books-cards-and-gifts':
      'Find literary treasures, greeting cards, and thoughtful gifts at our books, cards, and gifts shops in Clearwater, Roodepoort.',
    'cellular-service':
      'Stay connected with cellular service providers at Clearwater Mall. Get new phones, data plans, repairs, and accessories for all your mobile needs.',
    'centre-management':
      'Get in touch with our dedicated center management team at Clearwater, ensuring a seamless and enjoyable shopping experience in Roodepoort.',
    'department-stores':
      'Discover a world of choices at our department stores in Clearwater. Shop for fashion, home goods, and more, all under one roof in Roodepoort.',
    'eyewear-and-optometrists':
      'Enhance your vision and style with top eyewear brands and professional optometrists at Clearwater, Roodepoort. See the world clearly.',
    'fashion-and-footwear':
      'Elevate your style with the latest fashion trends and footwear collections at Clearwater, the ultimate shopping destination in Roodepoort.',
    'childrens-fashion':
      "Dress your kids in style with children's fashion stores at Clearwater Mall. Explore adorable and durable clothing for all ages, from infants to teens.",
    footwear:
      'Step out in style with the latest footwear at Clearwater Mall. Find shoes for every occasion, from trendy sneakers to elegant heels and comfortable boots.',
    'mens-fashion':
      "Elevate your style with men's fashion at Clearwater Mall. Explore trendy apparel, formal wear, and accessories for every occasion and personal taste.",
    sneakers:
      'Step up your shoe game with our diverse collection of sneakers at Clearwater. Find the perfect pair to elevate your style and comfort.',
    'unisex-fashion':
      'Unleash your fashion freedom with our unisex fashion offerings at Clearwater. Explore clothing and accessories designed for all genders.',
    'womens-fashion':
      "Explore the latest trends in women's fashion at Clearwater Mall. Shop stylish clothing, accessories, and designer wear for every occasion and style.",
    'food-and-beverages':
      "Indulge your taste buds with food and beverage options at Clearwater Mall. Explore diverse restaurants, cafes, and eateries for every craving.",
    'coffee-shops':
      "Enjoy a delightful coffee experience at Clearwater's coffee shops. Sip on freshly brewed coffee and indulge in sweet treats in a cozy atmosphere.",
    'fast-food-and-confectionary':
      "For a quick and tasty meal, head to Clearwater's fast food and confectionery outlets. Treat yourself to mouthwatering delights that satisfy cravings.",
    'groceries-and-supermarkets':
      "Stock up on essentials at Clearwater Mall's groceries & supermarkets. Find fresh produce, household items, and all your daily needs conveniently.",
    halaal:
      'Discover Halaal dining and shopping options at Clearwater Mall. Explore certified Halaal restaurants, food outlets, and stores for your convenience.',
    'health-food':
      "Nourish your body with health food options at Clearwater Mall. Shop organic products, supplements, and wholesome ingredients for a balanced lifestyle.",
    liquor:
      "Explore a wide selection of beverages at Clearwater Mall's liquor stores. Find wines, spirits, and beers for any occasion, responsibly.",
    restaurants:
      "Indulge your taste buds at Clearwater's restaurants. From casual dining to gourmet experiences, savor a variety of delicious cuisines.",
    speciality:
      'Explore unique and specialty shops at Clearwater. From artisanal crafts to rare finds, our speciality stores offer one-of-a-kind treasures.',
    'takeaway-and-delivery-services':
      "Grab a quick bite or get delivery from Clearwater Mall's takeaway services. Enjoy a variety of delicious meals and snacks on the go or delivered.",
    'health-hair-and-beauty':
      "Enhance your well-being at Clearwater Mall's health, hair, and beauty stores. Find products and services for ultimate pampering and care.",
    barber:
      'Find the best barber shops at Clearwater Mall. Experience premium grooming services, classic cuts, and modern styles for men in a relaxed atmosphere.',
    'beauty-salon':
      "Indulge in pampering at Clearwater Mall's beauty salons. Get professional treatments, from facials to manicures, for a radiant and refreshed look.",
    'fragrances-and-cosmetics':
      "Discover a world of scents and beauty at Clearwater's fragrances and cosmetics shops. Find the perfect products to enhance your natural beauty.",
    'hair-salon':
      'Discover top hair salons at Clearwater Mall. Get the perfect cut, colour, or style from expert stylists for a fresh new look. Book your appointment today!',
    'health-and-wellness':
      "Prioritize your health and well-being with a visit to Clearwater's health and wellness shops. Explore a range of products and services at Clearwater.",
    'home-and-decor':
      'Transform your living space with home and decor shops at Clearwater Mall. Discover stylish furniture, decor items, and essentials to beautify your home.',
    'jewellery-and-accessories':
      'Elevate your style with exquisite jewelry and fashionable accessories at Clearwater. Come and explore our diverse range of shops.',
    'movies-and-entertainment':
      'Get ready for a world of entertainment at Clearwater. Explore our shops that cater to movie enthusiasts and provide entertainment for all ages.',
    'new-stores':
      'Stay updated with the latest additions to Clearwater. Explore our new stores and be the first to experience fresh and exciting shopping opportunities.',
    'speciality-shops-and-services':
      "Discover unique finds at Clearwater Mall's speciality stores. Explore a curated selection of niche products and unique services you won't find anywhere else.",
    'leather-and-luggage':
      'Find quality leather goods and luggage at Clearwater Mall. Shop stylish handbags, wallets, and durable travel bags for every journey and occasion.',
    'pet-shops':
      "Pamper your furry friends with the best at Clearwater's pet shops. Explore a wide variety of pet products and services to keep them happy and healthy.",
    'tobacconists-and-e-cigarettes':
      "Visit tobacconists and e-cigarette shops at Clearwater Mall. Discover a range of tobacco products, vaping devices, and accessories from leading brands.",
    travel:
      "Plan your next adventure with travel agencies at Clearwater Mall. Find deals on flights, holidays, and comprehensive travel services for your dream trip.",
    'sport-fitness-and-lifestyle':
      'Achieve your goals with sport, fitness & lifestyle shops at Clearwater Mall. Find activewear, equipment, and gear from top brands for all your needs.',
    technology:
      "Explore the latest tech at Clearwater Mall's technology stores. Discover electronics, gadgets, and innovative solutions for home, work, and play.",
    'toys-and-hobbies':
      "Find the perfect gift at Clearwater's Toys and Hobbies shops. Dive into a world of fun with our selection of toys, games, and hobby supplies.",
  },

  // Glen
  glen: {
    default_title: 'Shops in Johannesburg | ' + environment.environment_title,
    default_description:
      'Explore a diverse array of shops at The Glen, offering everything from fashion and electronics to dining and entertainment.',
    title_extention: ' | Johannesburg | ' + environment.environment_title,

    'banks-and-forex':
      'Conveniently manage your finances at The Glen. Discover banks and forex services to meet your financial needs in Johannesburg.',
    'books-cards-and-gifts':
      'Find literary treasures, greeting cards, and thoughtful gifts at our books, cards, and gifts shops in The Glen, Johannesburg.',
    'cellular-service':
      'Stay connected with the latest mobile devices and service plans. Explore cellular service providers at The Glen in Johannesburg.',
    'centre-management':
      'Get in touch with our dedicated center management team at The Glen, ensuring a seamless and enjoyable shopping experience in Johannesburg.',
    'department-stores':
      'Discover a world of choices at our department stores in The Glen. Shop for fashion, home goods, and more, all under one roof in Johannesburg.',
    'eyewear-and-optometrists':
      'Enhance your vision and style with top eyewear brands and professional optometrists at The Glen, Johannesburg. See the world clearly.',
    'fashion-and-footwear':
      'Elevate your style with the latest fashion trends and footwear collections at The Glen, the ultimate shopping destination in Johannesburg.',
    'childrens-fashion':
      "Dress your little ones in style! Explore a delightful selection of children's fashion at The Glen, your go-to shopping spot in Johannesburg.",
    footwear:
      'Step into the world of quality footwear at The Glen, Johannesburg. Find the perfect pair for every occasion and step out in confidence.',
    'mens-fashion':
      "Discover the latest trends in men's fashion at The Glen. Explore a wide range of stylish clothing and accessories tailored for the modern man.",
    sneakers:
      'Step up your shoe game with our diverse collection of sneakers at The Glen. Find the perfect pair to elevate your style and comfort.',
    'unisex-fashion':
      'Unleash your fashion freedom with our unisex fashion offerings at The Glen. Explore clothing and accessories designed for all genders.',
    'womens-fashion':
      "Elevate your wardrobe with the latest in women's fashion at The Glen. From chic dresses to trendy accessories, find your perfect look here.",
    'food-and-beverages':
      "Satisfy your cravings at The Glen's food and beverage hub. Discover a wide array of dining options, from delectable meals to refreshing drinks.",
    'coffee-shops':
      "Enjoy a delightful coffee experience at The Glen's coffee shops. Sip on freshly brewed coffee and indulge in sweet treats in a cozy atmosphere.",
    'fast-food-and-confectionary':
      "For a quick and tasty meal, head to The Glen's fast food and confectionery outlets. Treat yourself to mouthwatering delights that satisfy cravings.",
    'groceries-and-supermarkets':
      "Shop for your daily essentials at The Glen's groceries and supermarkets. Find a variety of products and top-quality goods for your convenience.",
    halaal:
      'Discover a diverse range of Halaal dining options at The Glen. Enjoy delicious meals prepared in accordance with Islamic dietary guidelines.',
    'health-food':
      "Nourish your body with wholesome choices at The Glen's health food outlets. Find organic and nutritious options to support your well-being.",
    liquor:
      "Discover a wide selection of fine wines, spirits, and more at The Glen's liquor shops. Find the perfect drink for every occasion.",
    restaurants:
      "Indulge your taste buds at The Glen's restaurants. From casual dining to gourmet experiences, savor a variety of delicious cuisines.",
    speciality:
      'Explore unique and specialty shops at The Glen. From artisanal crafts to rare finds, our speciality stores offer one-of-a-kind treasures.',
    'takeaway-and-delivery-services':
      'Enjoy convenience with our takeaway and delivery services. Discover a range of culinary delights you can savor from the comfort of your own home at The Glen.',
    'health-hair-and-beauty':
      "Enhance your well-being and beauty at The Glen's health, hair, and beauty shops. Find professional services and quality products.",
    barber:
      'Visit our barber shop at The Glen for grooming services tailored to your style. Experience expert care and precision in a relaxed environment.',
    'beauty-salon':
      'Pamper yourself at our beauty salon in The Glen. Unwind and revitalize with a range of beauty treatments and services.',
    'fragrances-and-cosmetics':
      "Discover a world of scents and beauty at The Glen's fragrances and cosmetics shops. Find the perfect products to enhance your natural beauty.",
    'hair-salon':
      'Transform your hairstyle at our hair salon in The Glen. Experience the latest trends and expert styling from our professional hairdressers.',
    'health-and-wellness':
      "Prioritize your health and well-being with a visit to The Glen's health and wellness shops. Explore a range of products and services at The Glen",
    'home-and-decor':
      'Discover a world of style and inspiration for your home at The Glen. Explore our unique shops offering a wide range of home and decor products.',
    'jewellery-and-accessories':
      'Elevate your style with exquisite jewelry and fashionable accessories at The Glen. Come and explore our diverse range of shops.',
    'movies-and-entertainment':
      'Get ready for a world of entertainment at The Glen. Explore our shops that cater to movie enthusiasts and provide entertainment for all ages.',
    'new-stores':
      'Stay updated with the latest additions to The Glen. Explore our new stores and be the first to experience fresh and exciting shopping opportunities.',
    'speciality-shops-and-services':
      'Discover unique specialty shops and essential services at The Glen. Explore our diverse range of options to meet your specific needs and interests.',
    'leather-and-luggage':
      'Find the perfect blend of style and functionality in our leather and luggage shops at The Glen. Explore high-quality products to accompany you.',
    'pet-shops':
      "Pamper your furry friends with the best at The Glen's pet shops. Explore a wide variety of pet products and services to keep them happy and healthy.",
    'tobacconists-and-e-cigarettes':
      "Explore a world of flavors and options at The Glen's tobacconists and e-cigarette shops. ",
    travel:
      "Plan your next adventure with ease at The Glen's travel shops. Explore a range of services and products to make your travel experience unforgettable.",
    'sport-fitness-and-lifestyle':
      'Elevate your fitness and lifestyle with our wide range of sports and fitness shops. Explore products and services that help you lead a healthy and active life.',
    technology:
      'Explore the latest in cutting-edge technology at The Glen. Discover a wide range of electronic gadgets and devices at our technology stores.',
    'toys-and-hobbies':
      "Find the perfect gift at The Glen's Toys and Hobbies shops. Dive into a world of fun with our selection of toys, games, and hobby supplies.",
  },


  // Rosebank
  rosebank: {
    default_title: 'Explore Stores at Rosebank Mall | Shopping Directory',
    default_description:
      'Explore a diverse range of shops at Rosebank Mall, from fashion and technology to beauty and wellness.',
    title_extention: ' in Johannesburg | ' + environment.environment_title,

    'banks-and-forex':
      'Convenient banking and forex services at Rosebank Mall to meet all your financial needs in one place.',
    'books-cards-and-gifts':
      'Find the perfect book, card, or gift at Rosebank Mall—delightful options for every occasion.',
    'cellular-service':
      'Access top cellular services at Rosebank Mall for all your communication needs.',
    'centre-management':
      'Contact the Centre Management for inquiries, bookings, and assistance at Rosebank Mall.',
    'department-stores':
      "Find everything you need at Rosebank Mall's department stores, featuring a wide range of products for all ages.",
    'eyewear-and-optometrists':
      'Find stylish eyewear and professional optometrist services at Rosebank Mall to keep your vision sharp.',
    'fashion-and-footwear':
      'Discover the perfect blend of fashion and footwear at Rosebank Mall, featuring styles for everyone.',
    'childrens-fashion':
      "Dress your little ones in style with the latest children's fashion at Rosebank Mall.",
    footwear:
      'Step into style with an array of footwear options at Rosebank Mall—perfect for every occasion.',
    'mens-fashion':
      "Discover the latest trends in men's fashion at Rosebank Mall, tailored to suit every style.",
    sneakers:
      'Step into style with the latest sneaker collections at Rosebank Mall, perfect for every occasion.',
    'unisex-fashion':
      'Shop unisex fashion at Rosebank Mall for trendy and versatile clothing options for everyone.',
    'womens-fashion':
      "Stay fashionable with the latest trends in women's fashion at Rosebank Mall, tailored to your unique style.",
    'food-and-beverages':
      'Indulge in a variety of food and beverage options at Rosebank Mall—something delicious for everyone!',
    'coffee-shops':
      "Relax and recharge at one of Rosebank Mall's cozy coffee shops, perfect for a quick break or a catch-up with friends.",
    'fast-food-and-confectionary':
      'Satisfy your cravings with fast food and sweet treats at Rosebank Mall—quick and delicious options await!',
    'groceries-and-supermarkets':
      "Stock up on essentials at Rosebank Mall's grocery and supermarket shops—everything you need under one roof.",
    halaal:
      'Savor delicious halaal options at Rosebank Mall, catering to diverse culinary preferences.',
    'health-food':
      'Explore healthy food options at Rosebank Mall, catering to your wellness and dietary needs.',
    liquor:
      'Discover a wide selection of liquor at Rosebank Mall—perfect for celebrations and gatherings.',
    restaurants:
      "Enjoy a culinary adventure at Rosebank Mall's restaurants, offering a variety of delicious dining options.",
    speciality:
      "Find unique and specialty items at Rosebank Mall's curated shops, perfect for gifts and personal treasures.",
    'takeaway-and-delivery-services':
      'Enjoy convenient takeaway and delivery services at Rosebank Mall, bringing your favorite meals right to your door.',
    'health-hair-and-beauty':
      'Discover health, hair, and beauty products at Rosebank Mall to help you look and feel your best.',
    barber:
      'Visit our barbershop for a fresh haircut and grooming at Rosebank Mall—look sharp and feel great.',
    'beauty-salon':
      "Pamper yourself at Rosebank Mall's beauty salons, offering a range of services for your relaxation and enjoyment.",
    'fragrances-and-cosmetics':
      'Indulge in luxury with a range of fragrances and cosmetics available at Rosebank Mall.',
    'hair-salon':
      "Treat yourself to a fresh look at Rosebank Mall's hair salons, where expert stylists await to pamper you.",
    'health-and-wellness':
      'Prioritize your well-being with health and wellness products available at Rosebank Mall.',
    'home-and-decor':
      "Transform your living space with unique home decor finds at Rosebank Mall's specialty shops.",
    'jewellery-and-accessories':
      'Explore stunning jewelry and accessories at Rosebank Mall—elegant pieces for any outfit.',
    'movies-and-entertainment':
      'Explore the best in movies and entertainment at Rosebank Mall, where fun and excitement await.',
    'new-stores':
      'Discover the latest additions to Rosebank Mall with our exciting new stores, offering fresh products and experiences.',
    'speciality-shops-and-services':
      'Explore specialty shops and services at Rosebank Mall, offering unique finds and experiences.',
    'leather-and-luggage':
      'Shop for premium leather goods and luggage at Rosebank Mall—travel in style.',
    'pet-shops':
      "Shop for your furry friends at Rosebank Mall's pet shops, offering everything from food to toys.",
    'tobacconists-and-e-cigarettes':
      'Find premium tobaccos and e-cigarette products at Rosebank Mall, catering to enthusiasts and casual users alike.',
    travel:
      'Plan your next getaway with travel services and expert advice available at Rosebank Mall.',
    'sport-fitness-and-lifestyle':
      'Explore sports, fitness, and lifestyle products at Rosebank Mall to keep you active and stylish.',
    technology:
      'Discover cutting-edge technology products at Rosebank Mall, featuring the latest gadgets and accessories.',
    'toys-and-hobbies':
      'Find fun toys and hobbies for all ages at Rosebank Mall—perfect for gifts or self-indulgence.',
    'tips-and-tricks':
      'Explore our blog for helpful tips and tricks to enhance your shopping experience at Rosebank Mall.',

    page_titles: {
      'banks-and-forex': 'Banks & Forex Services at Rosebank Mall',
      barber: 'Barber Shops at Rosebank Mall | Grooming Services',
      'beauty-salon': 'Beauty Salons at Rosebank Mall | Pamper Yourself',
      'books-cards-and-gifts': 'Books, Cards & Gift Shops at Rosebank Mall',
      'cellular-service': 'Cellular Service Providers at Rosebank Mall',
      'centre-management': 'Centre Management | Rosebank Mall',
      'childrens-fashion': "Children's Fashion Stores at Rosebank Mall",
      'coffee-shops': 'Coffee Shops at Rosebank Mall | Relax and Recharge',
      'department-stores': 'Department Stores at Rosebank Mall',
      'eyewear-and-optometrists': 'Eyewear & Optometrists at Rosebank Mall',
      'fashion-and-footwear': 'Fashion & Footwear Stores at Rosebank Mall',
      'fast-food-and-confectionary':
        'Fast Food & Confectionery at Rosebank Mall',
      'food-and-beverages': 'Food & Beverage Outlets at Rosebank Mall',
      footwear: 'Footwear Stores at Rosebank Mall | Shoes for Every Style',
      'fragrances-and-cosmetics':
        'Fragrance & Cosmetics Stores at Rosebank Mall',
      'groceries-and-supermarkets': 'Groceries & Supermarkets at Rosebank Mall',
      'hair-salon': 'Hair Salons at Rosebank Mall | Styling & Grooming',
      halaal: 'Halaal Dining Options at Rosebank Mall',
      'health-and-wellness': 'Health & Wellness Stores at Rosebank Mall',
      'health-food': 'Health Food Stores at Rosebank Mall',
      'health-hair-and-beauty':
        'Health, Hair & Beauty Services at Rosebank Mall',
      'home-and-decor': 'Home & Decor Stores at Rosebank Mall',
      'jewellery-and-accessories':
        'Jewelry & Accessories Stores at Rosebank Mall',
      'leather-and-luggage': 'Leather & Luggage Stores at Rosebank Mall',
      liquor: 'Liquor Stores at Rosebank Mall',
      'mens-fashion': "Men's Fashion Stores at Rosebank Mall",
      'movies-and-entertainment': 'Movies & Entertainment at Rosebank Mall',
      'new-stores': 'New Stores at Rosebank Mall | Latest Shopping Additions',
      'pet-shops': 'Pet Shops at Rosebank Mall | Essentials for Pets',
      restaurants: 'Restaurants at Rosebank Mall | Dining Options',
      sneakers: 'Sneaker Stores at Rosebank Mall | Stylish Footwear',
      speciality: 'Specialty Shops at Rosebank Mall',
      'speciality-shops-and-services':
        'Specialty Shops & Services at Rosebank Mall',
      'sport-fitness-and-lifestyle':
        'Sport, Fitness & Lifestyle Stores at Rosebank Mall',
      'takeaway-and-delivery-services':
        'Takeaway & Delivery Services at Rosebank Mall',
      technology: 'Technology Stores at Rosebank Mall | Gadgets & More',
      'tobacconists-and-e-cigarettes':
        'Tobacconists & E-Cigarettes at Rosebank Mall',
      'toys-and-hobbies': 'Toy & Hobby Stores at Rosebank Mall',
      travel: 'Travel Services at Rosebank Mall | Plan Your Next Adventure',
      'unisex-fashion': 'Unisex Fashion Stores at Rosebank Mall',
      'womens-fashion': "Women's Fashion Stores at Rosebank Mall",
    },
  },

  // Somerset
  somerset: {
    default_title: 'Shops in Cape Town | ' + environment.environment_title,
    default_description:
      'Explore a diverse array of shops at Somerset Mall, offering everything from fashion and electronics to dining and entertainment.',
    title_extention: ' | Cape Town | ' + environment.environment_title,

    'banks-and-forex':
      'Conveniently manage your finances at Somerset Mall. Discover banks and forex services to meet your financial needs in Cape Town.',
    'books-cards-and-gifts':
      'Find literary treasures, greeting cards, and thoughtful gifts at our books, cards, and gifts shops in Somerset Mall, Cape Town.',
    'cellular-service':
      'Stay connected with the latest mobile devices and service plans. Explore cellular service providers at Somerset Mall in Cape Town.',
    'centre-management':
      'Get in touch with our dedicated center management team at Somerset Mall, ensuring a seamless and enjoyable shopping experience in Cape Town.',
    'department-stores':
      'Discover a world of choices at our department stores in Somerset Mall. Shop for fashion, home goods, and more, all under one roof in Cape Town.',
    'eyewear-and-optometrists':
      'Enhance your vision and style with top eyewear brands and professional optometrists at Somerset Mall, Cape Town. See the world clearly.',
    'fashion-and-footwear':
      'Elevate your style with the latest fashion trends and footwear collections at Somerset Mall, the ultimate shopping destination in Cape Town.',
    'childrens-fashion':
      "Dress your little ones in style! Explore a delightful selection of children's fashion at Somerset Mall, your go-to shopping spot in Cape Town.",
    footwear:
      'Step into the world of quality footwear at Somerset Mall, Cape Town. Find the perfect pair for every occasion and step out in confidence.',
    'mens-fashion':
      "Discover the latest trends in men's fashion at Somerset Mall. Explore a wide range of stylish clothing and accessories tailored for the modern man.",
    sneakers:
      'Step up your shoe game with our diverse collection of sneakers at Somerset Mall. Find the perfect pair to elevate your style and comfort.',
    'unisex-fashion':
      'Unleash your fashion freedom with our unisex fashion offerings at Somerset Mall. Explore clothing and accessories designed for all genders.',
    'womens-fashion':
      "Elevate your wardrobe with the latest in women's fashion at Somerset Mall. From chic dresses to trendy accessories, find your perfect look here.",
    'food-and-beverages':
      "Satisfy your cravings at Somerset Mall's food and beverage hub. Discover a wide array of dining options, from delectable meals to refreshing drinks.",
    'coffee-shops':
      "Enjoy a delightful coffee experience at Somerset Mall's coffee shops. Sip on freshly brewed coffee and indulge in sweet treats in a cozy atmosphere.",
    'fast-food-and-confectionary':
      "For a quick and tasty meal, head to Somerset Mall's fast food and confectionery outlets. Treat yourself to mouthwatering delights that satisfy cravings.",
    'groceries-and-supermarkets':
      "Shop for your daily essentials at Somerset Mall's groceries and supermarkets. Find a variety of products and top-quality goods for your convenience.",
    halaal:
      'Discover a diverse range of Halaal dining options at Somerset Mall. Enjoy delicious meals prepared in accordance with Islamic dietary guidelines.',
    'health-food':
      "Nourish your body with wholesome choices at Somerset Mall's health food outlets. Find organic and nutritious options to support your well-being.",
    liquor:
      "Discover a wide selection of fine wines, spirits, and more at Somerset Mall's liquor shops. Find the perfect drink for every occasion.",
    restaurants:
      "Indulge your taste buds at Somerset Mall's restaurants. From casual dining to gourmet experiences, savor a variety of delicious cuisines.",
    speciality:
      'Explore unique and specialty shops at Somerset Mall. From artisanal crafts to rare finds, our speciality stores offer one-of-a-kind treasures.',
    'takeaway-and-delivery-services':
      'Enjoy convenience with our takeaway and delivery services. Discover a range of culinary delights you can savor from the comfort of your own home at Somerset Mall.',
    'health-hair-and-beauty':
      "Enhance your well-being and beauty at Somerset Mall's health, hair, and beauty shops. Find professional services and quality products.",
    barber:
      'Visit our barber shop at Somerset Mall for grooming services tailored to your style. Experience expert care and precision in a relaxed environment.',
    'beauty-salon':
      'Pamper yourself at our beauty salon in Somerset Mall. Unwind and revitalize with a range of beauty treatments and services.',
    'fragrances-and-cosmetics':
      "Discover a world of scents and beauty at Somerset Mall's fragrances and cosmetics shops. Find the perfect products to enhance your natural beauty.",
    'hair-salon':
      'Transform your hairstyle at our hair salon in Somerset Mall. Experience the latest trends and expert styling from our professional hairdressers.',
    'health-and-wellness':
      "Prioritize your health and well-being with a visit to Somerset Mall's health and wellness shops. Explore a range of products and services at Somerset Mall.",
    'home-and-decor':
      'Discover a world of style and inspiration for your home at Somerset Mall. Explore our unique shops offering a wide range of home and decor products.',
    'jewellery-and-accessories':
      'Elevate your style with exquisite jewelry and fashionable accessories at Somerset Mall. Come and explore our diverse range of shops.',
    'movies-and-entertainment':
      'Get ready for a world of entertainment at Somerset Mall. Explore our shops that cater to movie enthusiasts and provide entertainment for all ages.',
    'new-stores':
      'Stay updated with the latest additions to Somerset Mall. Explore our new stores and be the first to experience fresh and exciting shopping opportunities.',
    'speciality-shops-and-services':
      'Discover unique specialty shops and essential services at Somerset Mall. Explore our diverse range of options to meet your specific needs and interests.',
    'leather-and-luggage':
      'Find the perfect blend of style and functionality in our leather and luggage shops at Somerset Mall. Explore high-quality products to accompany you.',
    'pet-shops':
      "Pamper your furry friends with the best at Somerset Mall's pet shops. Explore a wide variety of pet products and services to keep them happy and healthy.",
    'tobacconists-and-e-cigarettes':
      "Explore a world of flavors and options at Somerset Mall's tobacconists and e-cigarette shops. ",
    travel:
      "Plan your next adventure with ease at Somerset Mall's travel shops. Explore a range of services and products to make your travel experience unforgettable.",
    'sport-fitness-and-lifestyle':
      'Elevate your fitness and lifestyle with our wide range of sports and fitness shops. Explore products and services that help you lead a healthy and active life.',
    technology:
      'Explore the latest in cutting-edge technology at Somerset Mall. Discover a wide range of electronic gadgets and devices at our technology stores.',
    'toys-and-hobbies':
      "Find the perfect gift at Somerset Mall's Toys and Hobbies shops. Dive into a world of fun with our selection of toys, games, and hobby supplies.",
  },

  // Woodlands
  woodlands: {
    default_title: 'Shops in Pretoria | ' + environment.environment_title,
    default_description:
      'Explore a diverse array of shops at Woodlands Boulevard, offering everything from fashion and electronics to dining and entertainment.',
    title_extention: ' | Pretoria | ' + environment.environment_title,

    'banks-and-forex':
      'Conveniently manage your finances at Woodlands Boulevard. Discover banks and forex services to meet your financial needs in Pretoria.',
    'books-cards-and-gifts':
      'Find literary treasures, greeting cards, and thoughtful gifts at our books, cards, and gifts shops in Woodlands Boulevard, Pretoria.',
    'cellular-service':
      'Stay connected with the latest mobile devices and service plans. Explore cellular service providers at Woodlands Boulevard in Pretoria.',
    'centre-management':
      'Get in touch with our dedicated center management team at Woodlands Boulevard, ensuring a seamless and enjoyable shopping experience in Pretoria.',
    'department-stores':
      'Discover a world of choices at our department stores in Woodlands Boulevard. Shop for fashion, home goods, and more, all under one roof in Pretoria.',
    'eyewear-and-optometrists':
      'Enhance your vision and style with top eyewear brands and professional optometrists at Woodlands Boulevard, Pretoria. See the world clearly.',
    'fashion-and-footwear':
      'Elevate your style with the latest fashion trends and footwear collections at Woodlands Boulevard, the ultimate shopping destination in Pretoria.',
    'childrens-fashion':
      "Dress your little ones in style! Explore a delightful selection of children's fashion at Woodlands Boulevard, your go-to shopping spot in Pretoria.",
    footwear:
      'Step into the world of quality footwear at Woodlands Boulevard, Pretoria. Find the perfect pair for every occasion and step out in confidence.',
    'mens-fashion':
      "Discover the latest trends in men's fashion at Woodlands Boulevard. Explore a wide range of stylish clothing and accessories tailored for the modern man.",
    sneakers:
      'Step up your shoe game with our diverse collection of sneakers at Woodlands Boulevard. Find the perfect pair to elevate your style and comfort.',
    'unisex-fashion':
      'Unleash your fashion freedom with our unisex fashion offerings at Woodlands Boulevard. Explore clothing and accessories designed for all genders.',
    'womens-fashion':
      "Elevate your wardrobe with the latest in women's fashion at Woodlands Boulevard. From chic dresses to trendy accessories, find your perfect look here.",
    'food-and-beverages':
      "Satisfy your cravings at Woodlands Boulevard's food and beverage hub. Discover a wide array of dining options, from delectable meals to refreshing drinks.",
    'coffee-shops':
      "Enjoy a delightful coffee experience at Woodlands Boulevard's coffee shops. Sip on freshly brewed coffee and indulge in sweet treats in a cozy atmosphere.",
    'fast-food-and-confectionary':
      "For a quick and tasty meal, head to Woodlands Boulevard's fast food and confectionery outlets. Treat yourself to mouthwatering delights that satisfy cravings.",
    'groceries-and-supermarkets':
      "Shop for your daily essentials at Woodlands Boulevard's groceries and supermarkets. Find a variety of products and top-quality goods for your convenience.",
    halaal:
      'Discover a diverse range of Halaal dining options at Woodlands Boulevard. Enjoy delicious meals prepared in accordance with Islamic dietary guidelines.',
    'health-food':
      "Nourish your body with wholesome choices at Woodlands Boulevard's health food outlets. Find organic and nutritious options to support your well-being.",
    liquor:
      "Discover a wide selection of fine wines, spirits, and more at Woodlands Boulevard's liquor shops. Find the perfect drink for every occasion.",
    restaurants:
      "Indulge your taste buds at Woodlands Boulevard's restaurants. From casual dining to gourmet experiences, savor a variety of delicious cuisines.",
    speciality:
      'Explore unique and specialty shops at Woodlands Boulevard. From artisanal crafts to rare finds, our speciality stores offer one-of-a-kind treasures.',
    'takeaway-and-delivery-services':
      'Enjoy convenience with our takeaway and delivery services. Discover a range of culinary delights you can savor from the comfort of your own home at Woodlands.',
    'health-hair-and-beauty':
      "Enhance your well-being and beauty at Woodlands Boulevard's health, hair, and beauty shops. Find professional services and quality products.",
    barber:
      'Visit our barber shop at Woodlands Boulevard for grooming services tailored to your style. Experience expert care and precision in a relaxed environment.',
    'beauty-salon':
      'Pamper yourself at our beauty salon in Woodlands Boulevard. Unwind and revitalize with a range of beauty treatments and services.',
    'fragrances-and-cosmetics':
      "Discover a world of scents and beauty at Woodlands Boulevard's fragrances and cosmetics shops. Find the perfect products to enhance your natural beauty.",
    'hair-salon':
      'Transform your hairstyle at our hair salon in Woodlands Boulevard. Experience the latest trends and expert styling from our professional hairdressers.',
    'health-and-wellness':
      "Prioritize your health and well-being with a visit to Woodlands Boulevard's health and wellness shops. Explore a range of products and services at Woodlands.",
    'home-and-decor':
      'Discover a world of style and inspiration for your home at Woodlands Boulevard. Explore our unique shops offering a wide range of home and decor products.',
    'jewellery-and-accessories':
      'Elevate your style with exquisite jewelry and fashionable accessories at Woodlands Boulevard. Come and explore our diverse range of shops.',
    'movies-and-entertainment':
      'Get ready for a world of entertainment at Woodlands Boulevard. Explore our shops that cater to movie enthusiasts and provide entertainment for all ages.',
    'new-stores':
      'Stay updated with the latest additions to Woodlands Boulevard. Explore our new stores and be the first to experience fresh and exciting shopping opportunities.',
    'speciality-shops-and-services':
      'Discover unique specialty shops and essential services at Woodlands Boulevard. Explore our diverse range of options to meet your specific needs and interests.',
    'leather-and-luggage':
      'Find the perfect blend of style and functionality in our leather and luggage shops at Woodlands Boulevard. Explore high-quality products to accompany you.',
    'pet-shops':
      "Pamper your furry friends with the best at Woodlands Boulevard's pet shops. Explore a wide variety of pet products and services to keep them happy and healthy.",
    'tobacconists-and-e-cigarettes':
      "Explore a world of flavors and options at Woodlands Boulevard's tobacconists and e-cigarette shops. ",
    travel:
      "Plan your next adventure with ease at Woodlands Boulevard's travel shops. Explore a range of services and products to make your travel experience unforgettable.",
    'sport-fitness-and-lifestyle':
      'Elevate your fitness and lifestyle with our wide range of sports and fitness shops. Explore products and services that help you lead a healthy and active life.',
    technology:
      'Explore the latest in cutting-edge technology at Woodlands Boulevard. Discover a wide range of electronic gadgets and devices at our technology stores.',
    'toys-and-hobbies':
      "Find the perfect gift at Woodlands Boulevard's Toys and Hobbies shops. Dive into a world of fun with our selection of toys, games, and hobby supplies.",
  },

  // Table Bay
  tablebay: {
    default_title: 'Shops in Cape Town | ' + environment.environment_title,
    default_description:
      'Explore all the shops at Table Bay Mall. From fashion and tech to food and lifestyle, discover everything in one destination.',
    title_extention: ' | Cape Town | ' + environment.environment_title,

    'new-stores':
      'Discover the latest new stores at Table Bay Mall. Shop the freshest brands and exciting new arrivals today!',
    'beauty-salon':
      "Treat yourself at one of Table Bay Mall’s top beauty salons. Pampering services to help you look and feel your best.",
    technology:
      'Explore the best in technology at Table Bay Mall. Find gadgets, electronics, and tech accessories in one place.',
    'books-cards-and-gifts':
      'Browse books, cards, and gifts at Table Bay Mall. Perfect finds for every occasion and special moment.',
    'takeaway-and-delivery-services':
      "Enjoy delicious takeaway and delivery options from Table Bay Mall's top eateries. Convenience at your doorstep.",
    'food-and-beverages':
      'Discover food and beverage stores at Table Bay Mall. From snacks to gourmet treats, we have it all.',
    liquor:
      'Shop a wide selection of liquor and fine wines at Table Bay Mall. Perfect for any celebration.',
    'toys-and-hobbies':
      'Find toys, games, and hobby supplies at Table Bay Mall. Fun for kids and adults alike.',
    'jewellery-and-accessories':
      'Discover stylish jewellery and accessories at Table Bay Mall. Complete your look with our stunning collections.',
    'childrens-fashion':
      "Shop the latest children’s fashion at Table Bay Mall. Trendy and comfortable styles for kids.",
    'eyewear-and-optometrists':
      'Visit Table Bay Mall for quality eyewear and professional optometrist services. See better, look great.',
    'cellular-service':
      'Stay connected with leading cellular service providers at Table Bay Mall. Find the right plan and device.',
    speciality:
      'Explore unique speciality shops at Table Bay Mall. One-of-a-kind products and services await.',
    'centre-management':
      "Contact Table Bay Mall’s Centre Management team. We’re here to assist with your shopping centre enquiries.",
    'hair-salon':
      "Refresh your style at Table Bay Mall's professional hair salons. Cuts, colours, and treatments for all.",
    'banks-and-forex':
      'Access banking and forex services at Table Bay Mall. Convenient financial solutions under one roof.',
    'home-and-decor':
      'Find beautiful home and decor stores at Table Bay Mall. Transform your space with stylish pieces.',
    'mens-fashion':
      "Shop men's fashion at Table Bay Mall. Classic and modern styles to suit every occasion.",
    'department-stores':
      'Explore department stores at Table Bay Mall. Your destination for fashion, homeware, and more.',
    'new-chcbc':
      'Find beautiful home and decor stores at Table Bay Mall. Transform your space with stylish pieces.',
    'fast-food-and-confectionary':
      'Grab tasty fast food and sweet treats at Table Bay Mall. Satisfy every craving today.',
    'womens-fashion':
      "Discover women's fashion stores at Table Bay Mall. Shop the latest trends and timeless styles.",
    'fashion-and-footwear':
      'Shop fashion and footwear at Table Bay Mall. Complete your wardrobe with top brands.',
    'health-food':
      'Explore health food stores at Table Bay Mall. Nutritious options for a balanced lifestyle.',
    sneakers:
      'Find the latest sneakers at Table Bay Mall. Comfort, style, and performance in every pair.',
    'groceries-and-supermarkets':
      "Shop groceries and daily essentials at Table Bay Mall's supermarkets. Fresh and convenient.",
    'fragrances-and-cosmetics':
      'Discover fragrances and cosmetics at Table Bay Mall. Enhance your beauty with premium brands.',
    'pet-shops':
      'Visit pet shops at Table Bay Mall. Find supplies and treats for your furry friends.',
    'food-and-beverages-stores':
      'Browse food and beverage stores at Table Bay Mall. Tasty options for every palate.',
    'leather-and-luggage':
      'Shop quality leather goods and luggage at Table Bay Mall. Durable, stylish travel solutions.',
    restaurants:
      "Dine at Table Bay Mall's top restaurants. A variety of cuisines and flavours to enjoy.",
    'unisex-fashion':
      'Explore unisex fashion at Table Bay Mall. Stylish looks that suit everyone.',
    'car-dealership':
      'Discover car dealerships at Table Bay Mall. Browse vehicles and services from trusted brands.',
    barber:
      'Get a fresh cut at a barber at Table Bay Mall. Classic and modern grooming for men.',
    'speciality-shops-and-services':
      'Discover speciality shops and services at Table Bay Mall. Unique finds and expert services.',
    'tobacconists-and-e-cigarettes':
      'Shop tobacconists and e-cigarettes at Table Bay Mall. Quality products and accessories available.',
    'coffee-shops':
      "Enjoy a break at Table Bay Mall’s coffee shops. Great coffee and cosy spaces await.",
    footwear:
      'Shop stylish and comfortable footwear at Table Bay Mall. Shoes for every occasion.',
    'movies-and-entertainment':
      'Enjoy movies and entertainment at Table Bay Mall. Fun for the whole family.',
    'health-hair-and-beauty':
      'Discover health, hair, and beauty services at Table Bay Mall. Everything you need for self-care.',
    'sport-fitness-and-lifestyle':
      'Shop sport, fitness, and lifestyle stores at Table Bay Mall. Gear up for an active life.',
    halaal:
      'Explore halaal food options at Table Bay Mall. Delicious and certified meals for all.',
    'health-and-wellness':
      'Discover health and wellness stores at Table Bay Mall. Products and services for a healthier you.',
  },
};
